import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableShell } from "@/components/EntityManager";
import { Printer, Save, Trash2 } from "lucide-react";
import { inr2, fmtDate } from "@/lib/format";
import { printSupplementaryBill } from "@/lib/print";
import { toast } from "sonner";

const stripInv = (v) => String(v || "").replace(/^INV[-\s]?/i, "") || "—";

export default function SupplementaryBillForm() {
  const [bills, setBills] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [saved, setSaved] = useState([]);
  const [billId, setBillId] = useState("");
  const [received, setReceived] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSaved = () =>
    api.get("/supplementary-bills").then((r) => setSaved(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([api.get("/invoices"), api.get("/receipts")])
      .then(([b, r]) => {
        setBills(b.data);
        setReceipts(r.data);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    loadSaved();
  }, []);

  const bill = useMemo(() => bills.find((b) => b.id === billId), [bills, billId]);
  const originalAmount = Number(bill?.total || 0);
  const balance = Math.max(0, originalAmount - (Number(received) || 0));

  // auto pre-fill part payment already received from Money Receipts
  const onSelectBill = (id) => {
    setBillId(id);
    setBillingAddress(bills.find((b) => b.id === id)?.billing_customer_address || "");
    const paid = receipts
      .filter((r) => r.against_invoice_id === id)
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    setReceived(paid);
    if (paid > 0) toast.info(`Auto-filled ₹${paid.toLocaleString("en-IN")} already received (from Money Receipts)`);
  };

  const payload = () => ({
    customer_name: bill.customer_name,
    original_no: stripInv(bill.invoice_no),
    original_date: bill.issue_date,
    original_amount: originalAmount,
    received: Number(received) || 0,
    balance,
    remarks,
    billing_customer_address: billingAddress,
  });

  const printOnly = () => {
    if (!bill) return toast.info("Select the original bill first");
    printSupplementaryBill(payload());
  };

  const saveAndPrint = async () => {
    if (!bill) return toast.info("Select the original bill first");
    setSaving(true);
    try {
      const res = await api.post("/supplementary-bills", {
        original_invoice_id: bill.id,
        original_no: stripInv(bill.invoice_no),
        customer_name: bill.customer_name,
        original_amount: originalAmount,
        received: Number(received) || 0,
        balance,
        remarks,
        billing_customer_address: billingAddress,
      });
      toast.success(`Supplementary bill ${res.data.supp_no} saved`);
      printSupplementaryBill({ ...payload(), supp_no: res.data.supp_no });
      loadSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reprint = (s) =>
    printSupplementaryBill({
      customer_name: s.customer_name,
      original_no: s.original_no,
      original_amount: s.original_amount,
      received: s.received,
      balance: s.balance,
      remarks: s.remarks,
      billing_customer_address: s.billing_customer_address,
      supp_no: s.supp_no,
    });

  const removeSupp = async (s) => {
    if (!window.confirm(`Delete supplementary bill ${String(s.supp_no || "").replace(/^SUPP[-\s]?/i, "")}?`)) return;
    try {
      await api.delete(`/supplementary-bills/${s.id}`);
      toast.success("Supplementary bill deleted");
      loadSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="grid gap-5 p-6">
          <p className="text-sm text-muted-foreground">
            Raise a supplementary bill for the remaining balance after a part payment. The "Amount Received"
            auto-fills from Money Receipts recorded against the bill; adjust if needed.
          </p>
          <div className="grid gap-1.5 sm:max-w-[420px]">
            <Label>Original Bill</Label>
            <Select value={billId} onValueChange={onSelectBill}>
              <SelectTrigger data-testid="supp-bill-select"><SelectValue placeholder="Select a bill" /></SelectTrigger>
              <SelectContent>
                {bills.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {stripInv(b.invoice_no)} — {b.customer_name || "—"} ({inr2(b.total)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {bill && (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <Label>Original Amount</Label>
                <Input value={inr2(originalAmount)} readOnly disabled className="bg-slate-50 font-medium" data-testid="supp-original-amount" />
              </div>
              <div className="grid gap-1.5">
                <Label>Amount Received (Part Payment)</Label>
                <Input type="number" value={received} onChange={(e) => setReceived(e.target.value)} data-testid="supp-received" />
              </div>
              <div className="grid gap-1.5">
                <Label>Balance Due</Label>
                <Input value={inr2(balance)} readOnly disabled className="bg-amber-50 font-semibold text-amber-800" data-testid="supp-balance" />
              </div>
            </div>
          )}

          {bill && (
            <div className="grid gap-1.5">
              <Label>Billing Customer Address (shown on the bill)</Label>
              <Input value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="e.g. Sricity-517646" data-testid="supp-billing-customer-address" />
            </div>
          )}

          {bill && (
            <div className="grid gap-1.5">
              <Label>Remarks (optional)</Label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Balance against part-paid freight bill" />
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={saveAndPrint} disabled={!bill || saving} className="gap-2" data-testid="supp-save-print-btn">
              <Save className="h-4 w-4" /> Save &amp; Print
            </Button>
            <Button variant="outline" onClick={printOnly} disabled={!bill} className="gap-2" data-testid="supp-print-btn">
              <Printer className="h-4 w-4" /> Print (without saving)
            </Button>
          </div>
        </CardContent>
      </Card>

      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Saved Supplementary Bills</h3>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Supp No.", "Original Bill", "Customer", "Original", "Received", "Balance Due", ""].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-[0.12em] text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {saved.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No supplementary bills yet.</TableCell>
                </TableRow>
              )}
              {saved.map((s) => (
                <TableRow key={s.id} data-testid="supp-saved-row">
                  <TableCell className="font-medium">{String(s.supp_no || "").replace(/^SUPP[-\s]?/i, "")}</TableCell>
                  <TableCell>{s.original_no}</TableCell>
                  <TableCell>{s.customer_name || "—"}</TableCell>
                  <TableCell className="tabular-nums">{inr2(s.original_amount)}</TableCell>
                  <TableCell className="tabular-nums">{inr2(s.received)}</TableCell>
                  <TableCell className="font-semibold tabular-nums text-amber-700">{inr2(s.balance)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => reprint(s)} data-testid="supp-reprint-btn">
                      <Printer className="h-4 w-4" /> Reprint
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => removeSupp(s)} data-testid="supp-delete-btn" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </div>
    </div>
  );
}
